import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { safeNextPath } from '@/router/safeNext';

/**
 * Route meta contract.
 *
 * - `public: true`  — no auth required (login, 403 landing).
 * - `requiresAuth`  — any authenticated user may enter (default for
 *                     anything not marked `public`).
 * - `requiresRole`  — narrowest: only that exact role may enter.
 * - `title`         — Spanish section label (match the nav labels). Used
 *                     by the `afterEach` hook below to set
 *                     `document.title` so multiple open tabs are
 *                     distinguishable in the browser tab bar (partner
 *                     feedback: Google-Drive-style titles per section).
 */
declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean;
    requiresRole?: 'socio' | 'equipo_interno';
    title?: string;
  }
}

/**
 * Base document title brand. Every authenticated page renders as
 * `STARS — <SECCIÓN>`; routes without a `meta.title` fall back to the
 * bare brand so we never leak a stale previous-section title.
 */
const APP_TITLE_BRAND = 'STARS';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { title: 'Dashboard' },
  },
  // --- Budgets (Wave 4) ---
  {
    path: '/budgets',
    name: 'budgets',
    component: () => import('@/views/budgets/BudgetListView.vue'),
    meta: { title: 'Presupuestos' },
  },
  {
    path: '/budgets/new',
    name: 'budget-create',
    component: () => import('@/views/budgets/BudgetFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Nuevo Presupuesto' },
  },
  {
    path: '/budgets/:id',
    name: 'budget-detail',
    component: () => import('@/views/budgets/BudgetDetailView.vue'),
    meta: { title: 'Detalle de Presupuesto' },
  },
  {
    path: '/budgets/:id/edit',
    name: 'budget-edit',
    component: () => import('@/views/budgets/BudgetFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Editar Presupuesto' },
  },
  // --- Reports (Wave 5) ---
  {
    path: '/reports',
    name: 'reports',
    component: () => import('@/views/reports/ReportsView.vue'),
    meta: { title: 'Reportes' },
  },
  // --- Equipment catalog ---
  {
    path: '/equipment',
    name: 'equipment',
    component: () => import('@/views/equipment/EquipmentListView.vue'),
    meta: { title: 'Equipamiento' },
  },
  // --- Payments (Phase 2) ---
  {
    path: '/payments',
    name: 'payments',
    component: () => import('@/views/payments/PaymentListView.vue'),
    meta: { title: 'Cobros' },
  },
  {
    path: '/payments/new',
    name: 'payment-create',
    component: () => import('@/views/payments/PaymentFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Nuevo Cobro' },
  },
  {
    path: '/payments/:id',
    name: 'payment-detail',
    component: () => import('@/views/payments/PaymentDetailView.vue'),
    meta: { title: 'Detalle de Cobro' },
  },
  {
    path: '/payments/:id/edit',
    name: 'payment-edit',
    component: () => import('@/views/payments/PaymentFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Editar Cobro' },
  },
  // --- Expenses (Phase 2) ---
  {
    path: '/expenses',
    name: 'expenses',
    component: () => import('@/views/expenses/ExpenseListView.vue'),
    meta: { title: 'Gastos' },
  },
  {
    path: '/expenses/new',
    name: 'expense-create',
    component: () => import('@/views/expenses/ExpenseFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Nuevo Gasto' },
  },
  {
    path: '/expenses/:id',
    name: 'expense-detail',
    component: () => import('@/views/expenses/ExpenseDetailView.vue'),
    meta: { title: 'Detalle de Gasto' },
  },
  {
    path: '/expenses/:id/edit',
    name: 'expense-edit',
    component: () => import('@/views/expenses/ExpenseFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Editar Gasto' },
  },
  // --- Invoices (Phase 3) ---
  {
    path: '/invoices',
    name: 'invoices',
    component: () => import('@/views/invoices/InvoiceListView.vue'),
    meta: { title: 'Facturación' },
  },
  {
    path: '/invoices/new',
    name: 'invoice-create',
    component: () => import('@/views/invoices/InvoiceCreateView.vue'),
    meta: { requiresRole: 'socio', title: 'Nueva Factura' },
  },
  {
    // Batch-2 — upload-and-archive flow for issued invoices. Standalone
    // so cobro forms can deep-link into it with ?returnTo back to the
    // payment screen, mirroring the received-invoice equivalent.
    path: '/invoices/upload',
    name: 'invoice-upload',
    component: () => import('@/views/invoices/IssuedInvoiceUploadView.vue'),
    meta: { requiresRole: 'socio', title: 'Subir Factura Emitida' },
  },
  {
    path: '/invoices/received',
    name: 'received-invoices',
    component: () => import('@/views/invoices/ReceivedInvoiceListView.vue'),
    meta: { title: 'Facturas Recibidas' },
  },
  {
    path: '/invoices/received/new',
    name: 'received-invoice-create',
    component: () => import('@/views/invoices/ReceivedInvoiceCreateView.vue'),
    meta: { requiresRole: 'socio', title: 'Nueva Factura Recibida' },
  },
  {
    // Wave F — upload-and-extract flow. A standalone view so it can be
    // deep-linked from the gastos form ("Cargala acá") with a ?returnTo
    // query so the user lands back on the expense screen after save.
    path: '/invoices/received/upload',
    name: 'received-invoice-upload',
    component: () => import('@/views/invoices/ReceivedInvoiceUploadView.vue'),
    meta: { requiresRole: 'socio', title: 'Subir Factura Recibida' },
  },
  {
    path: '/invoices/received/:id',
    name: 'received-invoice-detail',
    component: () => import('@/views/invoices/ReceivedInvoiceDetailView.vue'),
    meta: { title: 'Detalle de Factura Recibida' },
  },
  {
    path: '/invoices/:id',
    name: 'invoice-detail',
    component: () => import('@/views/invoices/InvoiceDetailView.vue'),
    meta: { title: 'Detalle de Factura' },
  },
  // --- Finance views (Phase 2 Wave 2) ---
  {
    path: '/finance/cashbox',
    name: 'cashbox',
    component: () => import('@/views/finance/CashboxView.vue'),
    meta: { title: 'Caja' },
  },
  {
    path: '/finance/accounts-receivable',
    name: 'accounts-receivable',
    component: () => import('@/views/finance/AccountsReceivableView.vue'),
    meta: { title: 'Cuentas por Cobrar' },
  },
  {
    path: '/finance/accounts-payable',
    name: 'accounts-payable',
    component: () => import('@/views/finance/AccountsPayableView.vue'),
    meta: { title: 'Cuentas por Pagar' },
  },
  // --- Inventory (Phase 4) ---
  {
    path: '/inventory',
    name: 'inventory',
    component: () => import('@/views/inventory/InventoryListView.vue'),
    meta: { title: 'Inventario' },
  },
  {
    path: '/inventory/:id',
    name: 'inventory-detail',
    component: () => import('@/views/inventory/InventoryDetailView.vue'),
    meta: { title: 'Detalle de Inventario' },
  },
  // --- Providers (Wave C) ---
  {
    path: '/proveedores',
    name: 'providers',
    component: () => import('@/views/providers/ProviderListView.vue'),
    meta: { title: 'Proveedores' },
  },
  // --- Personnel (Phase 5) ---
  {
    path: '/personnel',
    name: 'personnel',
    component: () => import('@/views/personnel/PersonnelListView.vue'),
    meta: { title: 'Personal' },
  },
  {
    path: '/personnel/new',
    name: 'personnel-create',
    component: () => import('@/views/personnel/PersonnelFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Nuevo Personal' },
  },
  {
    path: '/personnel/:id',
    name: 'personnel-detail',
    component: () => import('@/views/personnel/PersonnelDetailView.vue'),
    meta: { title: 'Detalle de Personal' },
  },
  {
    path: '/personnel/:id/edit',
    name: 'personnel-edit',
    component: () => import('@/views/personnel/PersonnelFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Editar Personal' },
  },
  // --- Users ---
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/users/UserListView.vue'),
    meta: { requiresRole: 'socio', title: 'Usuarios' },
  },
  // --- Settings ---
  {
    path: '/settings/commissions',
    name: 'commission-config',
    component: () => import('@/views/settings/CommissionConfigView.vue'),
    meta: { requiresRole: 'socio', title: 'Comisiones' },
  },
  // --- Projects pipeline (Wave 3) ---
  {
    path: '/projects',
    name: 'projects',
    component: () => import('@/views/projects/ProjectPipelineView.vue'),
    meta: { title: 'Proyectos' },
  },
  {
    path: '/projects/new',
    name: 'project-create',
    component: () => import('@/views/projects/ProjectFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Nuevo Proyecto' },
  },
  {
    path: '/projects/:id',
    name: 'project-detail',
    component: () => import('@/views/projects/ProjectDetailView.vue'),
    meta: { title: 'Detalle de Proyecto' },
  },
  {
    path: '/projects/:id/edit',
    name: 'project-edit',
    component: () => import('@/views/projects/ProjectFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Editar Proyecto' },
  },
  {
    path: '/clients',
    name: 'clients',
    component: () => import('@/views/clients/ClientListView.vue'),
    meta: { title: 'Clientes' },
  },
  {
    path: '/clients/new',
    name: 'client-create',
    component: () => import('@/views/clients/ClientFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Nuevo Cliente' },
  },
  {
    path: '/clients/:id',
    name: 'client-detail',
    component: () => import('@/views/clients/ClientDetailView.vue'),
    meta: { title: 'Detalle de Cliente' },
  },
  {
    path: '/clients/:id/edit',
    name: 'client-edit',
    component: () => import('@/views/clients/ClientFormView.vue'),
    meta: { requiresRole: 'socio', title: 'Editar Cliente' },
  },
  {
    // Catch-all: send unknown paths back to root. The auth guard will
    // then bounce to /login if needed — preserve the attempted
    // fullPath as `next` so a user who deep-links to an unknown route
    // while logged out lands back on that path after login (rather
    // than silently being reset to /).
    path: '/:pathMatch(.*)*',
    redirect: (to) => ({ path: '/', query: { next: to.fullPath } }),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * Global auth + role guard.
 *
 * Flow on each navigation:
 *  1. Await boot refresh promise so the guard never races a page-reload
 *     refresh — without this, `isAuthenticated` is false until the
 *     /auth/refresh response arrives, causing a spurious redirect.
 *  2. If the route is public, allow.
 *  3. If the user is already authenticated, enforce role (if any).
 *  4. Otherwise, redirect to /login.
 */
router.beforeEach(async (to) => {
  const store = useAuthStore();
  await store.waitForBoot();

  // Authenticated users hitting /login should be sent to their
  // intended destination (or home). `next` is attacker-controllable,
  // so funnel it through the open-redirect hardening helper.
  if (to.name === 'login' && store.isAuthenticated) {
    return safeNextPath(to.query.next);
  }

  if (to.meta.public) return true;

  // When we bounce to login we want the user to land back on what
  // they actually deep-linked to. Prefer an already-present `?next=`
  // query (set by the catch-all for unknown paths) over the current
  // fullPath, so `/some-unknown-path` doesn't collapse to `/` in the
  // next param.
  const loginNext = safeNextPath(to.query.next, to.fullPath);

  if (!store.isAuthenticated) {
    return {
      name: 'login',
      query: { next: loginNext },
    };
  }

  // Role-based access. If a route declares `requiresRole`, only a user
  // with that exact role may enter; everyone else is sent to /403.
  if (to.meta.requiresRole && store.user?.role !== to.meta.requiresRole) {
    return { name: 'forbidden' };
  }

  return true;
});

/**
 * Tab-title hook.
 *
 * Partners asked for Google-Drive-style tab titles so multiple open
 * tabs are distinguishable ("STARS — FACTURACIÓN" vs "STARS —
 * PROYECTOS"). We uppercase the section label to match the screenshot
 * they shared, and fall back to the bare `STARS` brand when the route
 * has no `meta.title` (e.g. the public /login and /403 pages — those
 * should read as the plain app name, not a stale previous section).
 *
 * Using `afterEach` (not `beforeEach`) so the title only moves after a
 * navigation has actually resolved — a cancelled/redirected navigation
 * won't leave the tab on the aborted section.
 */
router.afterEach((to) => {
  const section = typeof to.meta.title === 'string' ? to.meta.title.trim() : '';
  document.title = section ? `${APP_TITLE_BRAND} — ${section.toUpperCase()}` : APP_TITLE_BRAND;
});
